import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const paperFold: TemplateRegistration = {
  meta: {
    id: 'paper-fold',
    name: 'Paper Fold',
    category: 'Minimal',
    description: 'Paper-like minimal design with subtle fold shadow accents',
    previewImage: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['IBM Plex Sans', 'Work Sans']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner paper-card">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="section">
        <div class="container">
          <div class="paper-card">
            <h2 class="section-title">About Us</h2>
            <p class="about-text">${safeAbout}</p>
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceItems = data.services.map((s, i) => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        const num = String(i + 1).padStart(2, '0');
        return `
          <div class="service-item paper-card">
            <span class="service-num">${num}</span>
            <div class="service-body">
              <h3>${sName}</h3>
              <p>${sDesc}</p>
              ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Our Services</h2>
          <div class="services-stack">${serviceItems}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => {
        return `<div class="gallery-item paper-card"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`;
      }).join('');
      sections.push(`
      <section id="gallery" class="section">
        <div class="container">
          <h2 class="section-title">Gallery</h2>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card">
            <span class="testimonial-body">&ldquo;${tContent}&rdquo;</span>
            <span class="testimonial-attribution">&mdash; ${tName}${tRole ? `, ${tRole}` : ''}</span>
            ${t.rating ? `<span class="testimonial-stars">${renderStars(t.rating)}</span>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-alt">
        <div class="container">
          <h2 class="section-title">What People Say</h2>
          <div class="testimonials-list">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item paper-card">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section">
        <div class="container">
          <h2 class="section-title">FAQ</h2>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row paper-card">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Pricing</h2>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section">
        <div class="container">
          <h2 class="section-title">Hours</h2>
          <div class="paper-card">
            <ul class="hours-list">${hoursList}</ul>
          </div>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Get In Touch</h2>
          <div class="contact-info">
            ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
            ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
            ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps</a>` : ''}
          </div>
          <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
            <input type="text" placeholder="Name" required>
            <input type="email" placeholder="Email" required>
            <button type="submit">Send</button>
          </form>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --primary: #333333; --bg: #F8F7F4; --bg-alt: #EFEDE8; --accent: #333333;
      --text: #1A1A1A; --text-muted: #6B6B6B;
      --font-heading: 'IBM Plex Sans', sans-serif; --font-body: 'Work Sans', sans-serif;
      --fold-shadow: 4px 4px 0 rgba(0,0,0,0.05);
      --shadow-sm: 0 1px 3px rgba(0,0,0,.06);
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.7; }
    .container { max-width: 900px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--primary); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .8; }

    /* PAPER CARD */
    .paper-card { background: #fff; padding: 32px; box-shadow: var(--fold-shadow); border: 1px solid #E8E6E1; }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(248,247,244,.96); backdrop-filter: blur(8px); border-bottom: 1px solid #E8E6E1; }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 16px 24px; max-width: 900px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.3rem; color: var(--text); font-weight: 700; letter-spacing: -0.5px; }
    .nav-links { display: flex; gap: 28px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .85rem; font-weight: 500; letter-spacing: 0.5px; }
    .nav-links a:hover { color: var(--text); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 24px; height: 2px; background: var(--text); margin: 5px 0; transition: .3s; }

    /* HERO - stepped/staircase effect */
    .hero { position: relative; min-height: 100vh; display: flex; align-items: center; overflow: hidden; background: var(--bg); }
    .hero-content { position: relative; z-index: 2; padding: 140px 24px 100px; max-width: 900px; margin: 0 auto; width: 100%; }
    .hero-step { transition: transform .3s; }
    .hero-step-1 { padding-left: 0; }
    .hero-step-2 { padding-left: 60px; margin-top: -8px; }
    .hero-step-3 { padding-left: 120px; margin-top: 8px; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(2.6rem, 6vw, 4.2rem); line-height: 1.15; margin-bottom: 0; font-weight: 700; color: var(--text); letter-spacing: -1px; }
    .hero-sub { font-size: 1.05rem; color: var(--text-muted); max-width: 520px; margin-bottom: 0; line-height: 1.8; }
    .hero-cta { display: inline-block; padding: 14px 36px; background: var(--text); color: var(--bg); font-family: var(--font-heading); font-size: .9rem; font-weight: 600; letter-spacing: 1px; border: none; cursor: pointer; transition: transform .2s; box-shadow: var(--fold-shadow); }
    .hero-cta:hover { transform: translateY(-2px); opacity: 1; color: var(--bg); }

    /* SECTIONS */
    .section { padding: 80px 0; }
    .section-alt { background: var(--bg-alt); }
    .section-title { font-family: var(--font-heading); font-size: clamp(1.6rem, 3.5vw, 2.2rem); font-weight: 700; margin-bottom: 40px; color: var(--text); letter-spacing: -0.5px; }

    /* ABOUT - drop-cap story */
    .about-text { font-size: 1.05rem; color: var(--text-muted); line-height: 1.9; }
    .about-text::first-letter { font-family: var(--font-heading); font-size: 3.5rem; font-weight: 700; float: left; line-height: 1; margin-right: 12px; margin-top: 4px; color: var(--text); }

    /* SERVICES - numbered list with large numbers */
    .services-stack { display: flex; flex-direction: column; gap: 20px; }
    .service-item { display: flex; gap: 24px; align-items: flex-start; }
    .service-num { font-family: var(--font-heading); font-size: 3rem; font-weight: 700; color: #D4D0C8; line-height: 1; flex-shrink: 0; min-width: 60px; }
    .service-icon { display: none; }
    .icon-char { font-size: 24px; line-height: 1; color: var(--text); }
    .service-body h3 { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 600; margin-bottom: 6px; color: var(--text); }
    .service-body p { color: var(--text-muted); font-size: .95rem; line-height: 1.7; }
    .service-price { display: inline-block; margin-top: 8px; font-weight: 600; font-size: 1rem; color: var(--text); }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
    .gallery-item img { width: 100%; height: 200px; object-fit: cover; display: block; }

    /* TESTIMONIALS - minimal inline */
    .testimonials-list { display: flex; flex-direction: column; gap: 28px; max-width: 700px; }
    .testimonial-card { padding: 0; background: transparent; box-shadow: none; border: none; }
    .testimonial-body { font-size: 1.05rem; font-style: italic; color: var(--text-muted); line-height: 1.8; margin-bottom: 8px; display: inline; }
    .testimonial-attribution { font-style: normal; color: var(--text); font-size: .9rem; font-weight: 600; }
    .testimonial-stars { display: inline-block; margin-left: 8px; }
    .star { color: #D4D0C8; font-size: 1rem; }
    .star.filled { color: #333333; }

    /* FAQ */
    .faq-list { display: flex; flex-direction: column; gap: 16px; max-width: 700px; }
    .faq-q { font-family: var(--font-heading); font-size: 1.05rem; font-weight: 600; margin-bottom: 8px; color: var(--text); }
    .faq-a { color: var(--text-muted); line-height: 1.7; }

    /* PRICE LIST */
    .price-list { display: flex; flex-direction: column; gap: 16px; max-width: 700px; }
    .price-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
    .price-info h3 { font-family: var(--font-heading); font-size: 1.05rem; font-weight: 600; margin-bottom: 4px; color: var(--text); }
    .price-info p { color: var(--text-muted); font-size: .9rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.3rem; color: var(--text); white-space: nowrap; font-weight: 700; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid #E8E6E1; color: var(--text-muted); font-size: .95rem; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--text); padding: 16px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: #fff; font-size: 1.2rem; }
    .review-text { color: #fff; font-weight: 600; font-size: .9rem; }

    /* PROMO */
    .promo-banner { background: var(--bg); padding: 20px 0; }
    .promo-inner { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
    .promo-badge { background: var(--text); color: var(--bg); font-family: var(--font-heading); padding: 8px 20px; font-size: .9rem; font-weight: 600; }
    .promo-content strong { display: block; margin-bottom: 2px; color: var(--text); }
    .promo-content span { color: var(--text-muted); font-size: .9rem; }

    /* CONTACT - minimal inline form */
    .contact-info { display: flex; gap: 24px; flex-wrap: wrap; margin-bottom: 28px; }
    .contact-item { display: flex; align-items: center; gap: 8px; color: var(--text-muted); font-size: .9rem; }
    .contact-item .icon-char { color: var(--text); flex-shrink: 0; font-size: 18px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { font-weight: 600; font-size: .85rem; letter-spacing: 0.5px; color: var(--text); }
    .contact-form { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; }
    .contact-form input { background: var(--bg); border: 1px solid #D4D0C8; color: var(--text); padding: 12px 14px; font-family: var(--font-body); font-size: .9rem; outline: none; transition: border-color .2s; flex: 1; min-width: 140px; }
    .contact-form textarea { display: none; }
    .contact-form input:focus { border-color: var(--text); }
    .contact-form button { padding: 12px 28px; background: var(--text); color: var(--bg); font-family: var(--font-heading); font-size: .85rem; font-weight: 600; letter-spacing: 1px; border: none; cursor: pointer; transition: transform .2s; box-shadow: var(--fold-shadow); white-space: nowrap; }
    .contact-form button:hover { transform: translateY(-2px); }

    /* FOOTER */
    .footer { background: var(--text); padding: 40px 0; text-align: center; }
    .footer p { color: #A8A8A8; font-size: .85rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(248,247,244,.98); border-bottom: 1px solid #E8E6E1; padding: 16px 24px; gap: 16px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .service-item { flex-direction: column; gap: 12px; }
      .price-row { flex-direction: column; gap: 8px; }
      .hero-step-2 { padding-left: 24px; }
      .hero-step-3 { padding-left: 48px; }
      .contact-form { flex-direction: column; }
      .contact-form input { min-width: 100%; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero-content { padding: 110px 16px 60px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-content">
      <div class="hero-step hero-step-1">
        <h1>${safeHeadline}</h1>
      </div>
      <div class="hero-step hero-step-2">
        <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
      </div>
      <div class="hero-step hero-step-3">
        ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Get In Touch</a>` : ''}
      </div>
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
